import { useEffect, useState } from 'react';
import { ErrorSummary, FormRadioGroup, InPageAlert } from '@snsw/react-component-library';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { OrganisationHolderFields } from './organisation/OrganisationHolderFields';
import { IndividualHolderFields } from './individual/IndividualHolderFields';
import ScrollToTop from '../../../shared/utils/scrollToTop';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import { PagePath } from 'shared/constants/pagePath';
import { ItemRegistrationHolderPage } from 'plantItemNew/constants/itemRegistrationHolderPage';

import { RegistrationHolderTypes } from 'shared/constants/registrationHolderTypes';
import { createApplication, saveApplication } from 'shared/redux/applicationSlice';
import { PasswordlessContinueSaveExitButtons } from 'shared/components/formNavigationButtons/PasswordlessContinueSaveExitButtons';
import { getPlantItemlLicenceTypeCode } from 'shared/utils/Helpers';
import { store } from 'shared/redux/configureStore';
import { storeHolderDetails } from 'plantItemNew/redux/slices/holderDetailsSlice';
import { storeApplicantEmail, storeApplicantNames } from 'plantItemNew/redux/slices/applicantDetailsSlice';
import styles from './itemRegistrationHolder.module.scss';

export const ItemRegistrationHolder = (props) => {
  ScrollToTop();

  const dispatch = useDispatch();
  const application = useSelector((state) => state.application);
  const savedHolderDetails = useSelector((state) => state.holderDetails);
  const navigate = useNavigate();
  const user = useSelector((state) => state.passwordlessOidc.user);
  const [applicantEmail, setApplicantEmail] = useState('');

  const plantItemLicenceTypes = useSelector((state) => state.plantItemLicenceTypes);
  const plantItemLicenceCode = getPlantItemlLicenceTypeCode(plantItemLicenceTypes);

  const registrationTypeOptions = [
    {
      value: RegistrationHolderTypes.organisation,
      label: 'Organisation',
      clarify: 'You have an ABN or ACN.',
    },
    {
      value: RegistrationHolderTypes.individual,
      label: 'Individual',
      clarify: 'You are not a business, sole trader or partnership.',
    },
  ];

  const holderComponentRef = React.useRef();

  const savedRegistrationTypeValue = savedHolderDetails?.registrationType ?? '';

  const [registrationTypeValue, setRegistrationTypeValue] = useState(savedRegistrationTypeValue);
  const [errors, setErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [holderDetails, setHolderDetails] = useState(savedHolderDetails);
  const [errorsSummary, setErrorsSummary] = useState([]);
  const [holderErrorSummary, setHolderErrorSummary] = useState([]);
  const [combinedErrorSummary, setCombinedErrorSummary] = useState([]);

  useEffect(() => {
    setApplicantEmail(user.profile.email);
    dispatch(storeApplicantEmail({ emailAddress: user.profile.email }));
  }, [user]);

  useEffect(() => {
    if (showErrors) validateExt(validators, setErrors, setErrorsSummary);
  }, [registrationTypeValue]);

  useEffect(() => {
    setCombinedErrorSummary([].concat(errorsSummary, holderErrorSummary));
  }, [errorsSummary, holderErrorSummary]);

  const onChangeEvent = (v) => {
    setRegistrationTypeValue(v);
  };

  const displayRegistrationFields = (registrationType) => {
    if (registrationType === RegistrationHolderTypes.organisation) {
      return (
        <OrganisationHolderFields
          ref={holderComponentRef}
          onChange={setHolderDetails}
          value={holderDetails}
          onErrorChange={setHolderErrorSummary}
        />
      );
    } else if (registrationType === RegistrationHolderTypes.individual) {
      return (
        <IndividualHolderFields
          ref={holderComponentRef}
          onChange={setHolderDetails}
          value={holderDetails}
          onErrorChange={setHolderErrorSummary}
          applicantEmail={applicantEmail}
        />
      );
    }
  };

  const validators = [
    {
      fieldName: 'registrationTypeValue',
      fieldValue: registrationTypeValue,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Type of registration holder is required.',
    },
  ];

  const validateAll = () => {
    let isCurrentComponentValid = validateExt(validators, setErrors, setErrorsSummary);

    let isHolderValid = false;
    if (holderComponentRef.current) isHolderValid = holderComponentRef.current.validate();

    return isCurrentComponentValid && isHolderValid;
  };

  const storeDetails = (isCompleted) => {
    let details = {
      isCompleted,
      registrationType: registrationTypeValue,
      ...holderDetails,
    };

    dispatch(storeHolderDetails(details));

    const applicantNames = details.amIRegistrationHolder ? details : {};
    dispatch(storeApplicantNames(applicantNames));
  };

  const onContinueClick = async () => {
    let isCompleted = validateAll();

    if (isCompleted) {
      storeDetails(true);
      await (application?.applicationId ? dispatchSaveApplication() : dispatchCreateApplication());
      return;
    }

    setShowErrors(true);
    window.scrollTo(0, 0);
  };

  const dispatchCreateApplication = async () => {
    await store.dispatch(createApplication(plantItemLicenceCode)).unwrap();
    onApplicationSaved();
  };

  const dispatchSaveApplication = async () => {
    await store.dispatch(saveApplication(true)).unwrap();
    onApplicationSaved();
  };

  const onApplicationSaved = () => {
    setShowErrors(false);
    navigate(PagePath.plantItemList);
  };

  const onPreSaving = () => {
    let completed = validateAll();
    storeDetails(completed);
  };

  return (
    <>
      <ErrorSummary errors={combinedErrorSummary} />
      <InPageAlert className={styles.marginNegTop} variant="info">
        <p className={styles.pText}>
          To align registration dates for all plant items at a single location and qualify for a discounted renewal fee
          next year, please email <a href="mailto:licensing@safework.nsw.gov.au">licensing@safework.nsw.gov.au</a> with
          your plant item details and location.
        </p>
      </InPageAlert>
      <FormRadioGroup
        id="registrationTypeValue"
        name="registrationTypeValue"
        vertical={true}
        options={registrationTypeOptions}
        onChange={onChangeEvent}
        value={registrationTypeValue}
        legend={ItemRegistrationHolderPage.registrationTypeLabel}
        errorMessage={errors?.registrationTypeValue?.errorMessage}
        hasError={showErrors && errors?.registrationTypeValue?.invalid}
      />

      {displayRegistrationFields(registrationTypeValue)}
      <PasswordlessContinueSaveExitButtons
        onContinueClick={onContinueClick}
        onPreSaving={onPreSaving}
        licenceType={plantItemLicenceCode}
      />
    </>
  );
};
